/**
 * BandPage.jsx – Gemeinsame Logik für Auffüllen und Reinigungsplan
 *
 * Admin/SL: ausklappbarer Konfig-Bereich (Kategorien anlegen, reordern, Multi-Standort)
 * Alle: Arbeitsansicht (aktuelles Element, Status, Buttons, Historie)
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { TagesgeschaeftShareBar } from './TagesgeschaeftShareBar';
import {
  ChevronDown, ChevronUp, Plus, Trash2, X, Check, Loader2,
  MapPin, Settings, PlayCircle, CheckSquare, ArrowRight, RefreshCw,
  User, History, GripVertical, ArrowUp, ArrowDown, AlertCircle, CalendarDays,
  ImageIcon, Eye,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/** Datum + Uhrzeit vollständig: "TT.MM.JJJJ, HH:MM Uhr" */
function fmtDateTime(dateStr) {
  const d = new Date(dateStr);
  const date = d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time} Uhr`;
}

/** iOS-sicherer Bild-Upload — identisch mit TagesgeschaeftPage */
async function uploadImageiOS(url, file) {
  let buffer;
  if (typeof file.arrayBuffer === 'function') {
    buffer = await file.arrayBuffer();
  } else {
    buffer = await new Promise((res, rej) => {
      const reader = new FileReader();
      reader.onload = () => res(reader.result);
      reader.onerror = rej;
      reader.readAsArrayBuffer(file);
    });
  }
  const blob = new Blob([buffer], { type: file.type || 'image/jpeg' });
  const fd = new FormData();
  fd.append('file', blob, file.name || 'image.jpg');
  const h = new Headers();
  h.set('Authorization', `Bearer ${localStorage.getItem('token')}`);
  const resp = await fetch(url, { method: 'POST', headers: h, body: fd });
  if (!resp.ok) {
    const err = await resp.json().catch(() => ({}));
    const d = err.detail;
    throw new Error(Array.isArray(d) ? d.map(x => x.msg).join('; ') : (d || `HTTP ${resp.status}`));
  }
  return resp.json();
}

// ── Standort-Selector ─────────────────────────────────────────────────────────
function LocationSelector({ locations, value, onChange }) {
  if (!locations.length) return null;
  if (locations.length === 1) return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <MapPin className="h-3.5 w-3.5 text-primary" />
      <span className="font-medium text-foreground">{locations.find(l => l.id === value)?.name}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-1.5">
      <MapPin className="h-3.5 w-3.5 text-primary shrink-0" />
      <select value={value} onChange={e => onChange(e.target.value)}
        className="h-8 rounded-md border border-input bg-background px-3 text-sm focus:ring-2 focus:ring-primary focus:outline-none"
        data-testid="band-location-selector">
        {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
    </div>
  );
}

// ── Arbeitsansicht: Rollierende Liste ─────────────────────────────────────────
function BandArbeitsansicht({ band, onStart, onFinish, onLightbox }) {
  if (!band?.id) return (
    <div className="text-center py-8 text-muted-foreground">
      <AlertCircle className="h-8 w-8 mx-auto mb-2 opacity-40" />
      <p className="text-sm">Kein Band konfiguriert</p>
      <p className="text-xs mt-1">Admin/SL kann es oben anlegen</p>
    </div>
  );

  const cats = band.categories || [];
  if (!cats.length) return (
    <div className="text-center py-6 text-muted-foreground text-sm">Noch keine Einträge konfiguriert</div>
  );

  const curId = band.current_category_id;
  const curIdx = cats.findIndex(c => c.id === curId);
  const curCat = curIdx >= 0 ? cats[curIdx] : cats[0];
  const actualIdx = curIdx >= 0 ? curIdx : 0;
  const status = band.current_status || 'offen';

  // Letzte 4 erledigte aus history (älteste → neueste Reihenfolge in UI)
  const history = band.history || [];
  const last4 = history.slice(-4); // bereits in zeitlicher Reihenfolge (älteste zuerst)

  // Noch kommende Kategorien (alles NACH dem aktuellen)
  const upcoming = cats.slice(actualIdx + 1);

  return (
    <div className="space-y-1" data-testid="band-arbeitsansicht">
      {/* ERLEDIGTE (letzte 4, von oben nach unten älteste → neueste) */}
      {last4.map((h, i) => (
        <div key={`done-${i}`}
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-muted/20 border border-border/40 opacity-60"
          data-testid="band-done-item">
          <CheckSquare className="h-4 w-4 text-green-500 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium line-through text-muted-foreground truncate">{h.category_name}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {h.finished_by_name}
              {h.finished_at && (
                <span className="ml-1 opacity-75">
                  · {fmtDateTime(h.finished_at)}                </span>
              )}
            </p>
          </div>
        </div>
      ))}

      {/* AKTUELL — hervorgehoben */}
      <div className="rounded-xl border-2 border-primary/60 bg-primary/5 dark:bg-primary/10 px-4 py-4 space-y-3"
        data-testid="band-current-item">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <p className="font-bold text-base leading-snug">{curCat?.name}</p>
            {curCat?.description && (
              <p className="text-sm text-muted-foreground mt-0.5">{curCat.description}</p>
            )}
            {/* Referenzbild-Thumbnail */}
            {curCat?.reference_image_url && (
              <button
                onClick={() => onLightbox && onLightbox(curCat.reference_image_url)}
                className="mt-2 block rounded-lg overflow-hidden border hover:ring-2 hover:ring-primary transition-all w-12 h-12"
                title="Bild ansehen" data-testid="band-ref-image">
                <img src={curCat.reference_image_url} alt="Referenz"
                  className="w-full h-full object-cover"
                  onError={e => { e.target.style.display='none'; e.currentTarget.parentElement.style.display='none'; }} />
              </button>
            )}
          </div>
          <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${
            status === 'in_bearbeitung'
              ? 'bg-amber-100 text-amber-700 animate-pulse dark:bg-amber-900/30 dark:text-amber-300'
              : 'bg-primary/10 text-primary'
          }`}>
            {status === 'in_bearbeitung' ? 'In Bearbeitung' : 'Aktuell'}
          </span>
        </div>

        {/* Übergabe-Info */}
        {status === 'in_bearbeitung' && band.current_started_by_name && (
          <div className="flex items-center gap-2 text-xs bg-amber-50 dark:bg-amber-900/20 rounded-lg px-3 py-2 text-amber-700 dark:text-amber-400">
            <User className="h-3.5 w-3.5 shrink-0" />
            <span>
              <strong>{band.current_started_by_name}</strong> hat angefangen
              {band.current_started_at && (
                <span className="ml-1 opacity-75">
                  · {fmtDateTime(band.current_started_at)}
                </span>
              )}
            </span>
          </div>
        )}

        {/* Aktions-Buttons */}
        <div className="flex gap-2">
          {status === 'offen' && (
            <Button variant="outline" size="sm" className="flex-1 gap-1.5"
              onClick={() => onStart(band.id)} data-testid="band-anfangen-btn">
              <PlayCircle className="h-4 w-4" /> Anfangen
            </Button>
          )}
          <Button size="sm" className="flex-1 gap-1.5"
            onClick={() => onFinish(band.id)} data-testid="band-fertig-btn">
            <CheckSquare className="h-4 w-4" />
            {status === 'in_bearbeitung' ? 'Fertig' : 'Direkt Fertig'}
          </Button>
        </div>
      </div>

      {/* NOCH KOMMEND — gedimmt, nicht klickbar */}
      {upcoming.map((cat, i) => (
        <div key={cat.id}
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-card border border-border/30 opacity-40"
          data-testid="band-upcoming-item">
          <RefreshCw className="h-4 w-4 text-muted-foreground shrink-0" style={{ opacity: Math.max(0.3, 1 - i * 0.15) }} />
          <p className="text-sm text-muted-foreground truncate">{cat.name}</p>
        </div>
      ))}

      {/* Zyklus-Wrap-Hinweis wenn aktuell = letzter Eintrag */}
      {actualIdx === cats.length - 1 && (
        <div className="flex items-center gap-2 px-4 py-2 text-xs text-muted-foreground/60">
          <RefreshCw className="h-3 w-3 shrink-0" />
          <span>Danach startet Runde {(band.cycle_count || 0) + 2} wieder von vorne</span>
        </div>
      )}
    </div>
  );
}

// ── Kategorie-Liste mit Drag-Reorder (touch-tauglich: ↑↓ + drag) ────────────
function CategoryList({ categories, onChange }) {
  const dragIdx = useRef(null);
  const [draggingIdx, setDraggingIdx] = useState(null);

  const moveUp = (i) => {
    if (i === 0) return;
    const n = [...categories];
    [n[i - 1], n[i]] = [n[i], n[i - 1]];
    onChange(n);
  };
  const moveDown = (i) => {
    if (i >= categories.length - 1) return;
    const n = [...categories];
    [n[i], n[i + 1]] = [n[i + 1], n[i]];
    onChange(n);
  };
  const removeItem = (i) => onChange(categories.filter((_, j) => j !== i));
  const updateItem = (i, field, val) => {
    const n = [...categories];
    n[i] = { ...n[i], [field]: val };
    onChange(n);
  };

  return (
    <div className="space-y-2">
      {categories.map((cat, i) => (
        <div key={i} draggable
          onDragStart={() => { dragIdx.current = i; setDraggingIdx(i); }}
          onDragOver={e => { e.preventDefault(); }}
          onDrop={() => {
            const from = dragIdx.current;
            if (from === null || from === i) { dragIdx.current = null; setDraggingIdx(null); return; }
            const n = [...categories];
            const [moved] = n.splice(from, 1);
            n.splice(i, 0, moved);
            onChange(n);
            dragIdx.current = null; setDraggingIdx(null);
          }}
          onDragEnd={() => { dragIdx.current = null; setDraggingIdx(null); }}
          className={`flex gap-2 items-start bg-muted/30 rounded-xl p-2 border transition-colors ${draggingIdx === i ? 'border-primary opacity-50' : 'border-border'}`}
          data-testid={`cat-row-${i}`}
        >
          {/* Drag handle + ↑↓ buttons */}
          <div className="flex flex-col items-center gap-0.5 pt-1 shrink-0">
            <GripVertical className="h-4 w-4 text-muted-foreground cursor-grab" />
            <button onClick={() => moveUp(i)} disabled={i === 0}
              className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30">
              <ArrowUp className="h-3 w-3" />
            </button>
            <button onClick={() => moveDown(i)} disabled={i >= categories.length - 1}
              className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-30">
              <ArrowDown className="h-3 w-3" />
            </button>
          </div>
          {/* Felder */}
          <div className="flex-1 space-y-1.5">
            <Input value={cat.name} onChange={e => updateItem(i, 'name', e.target.value)}
              placeholder="Name (z.B. Süßwaren, Kühlregal)" className="h-8 text-sm"
              data-testid={`cat-name-${i}`} />
            <Input value={cat.description || ''} onChange={e => updateItem(i, 'description', e.target.value)}
              placeholder="Beschreibung (optional)" className="h-7 text-xs" />
          </div>
          <button onClick={() => removeItem(i)} className="shrink-0 mt-1 text-muted-foreground hover:text-destructive">
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="w-full gap-1.5"
        onClick={() => onChange([...categories, { name: '', description: '' }])}>
        <Plus className="h-3.5 w-3.5" /> Eintrag hinzufügen
      </Button>
    </div>
  );
}

// ── Admin-Konfig-Panel ────────────────────────────────────────────────────────
function AdminKonfig({ bandType, locations, currentLocationId, onCreatedOrUpdated, existingBand, onRefImageUploaded }) {
  const [showKonfig, setShowKonfig] = useState(false);
  const [selectedLocIds, setSelectedLocIds] = useState([currentLocationId].filter(Boolean));
  const [categories, setCategories] = useState(
    existingBand?.categories?.map(c => ({ name: c.name, description: c.description || '' })) || []
  );
  const [title, setTitle] = useState(existingBand?.title || '');
  const [saving, setSaving] = useState(false);
  const [uploadingCatId, setUploadingCatId] = useState(null);
  const imgRefs = useRef({});

  useEffect(() => {
    if (existingBand) {
      setCategories(existingBand.categories?.map(c => ({ name: c.name, description: c.description || '' })) || []);
      setTitle(existingBand.title || '');
    }
  }, [existingBand?.id]);

  useEffect(() => {
    if (!existingBand && currentLocationId) {
      setSelectedLocIds(prev => prev.includes(currentLocationId) ? prev : [currentLocationId]);
    }
  }, [currentLocationId, existingBand]);

  const toggleLoc = (id) => setSelectedLocIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const uploadCategoryImage = async (catId, file) => {
    if (!existingBand) return;
    setUploadingCatId(catId);
    try {
      await uploadImageiOS(
        `${API}/api/tasks/band/${existingBand.id}/category/${catId}/upload-image`,
        file
      );
      toast.success('Referenzbild hochgeladen');
      onRefImageUploaded && onRefImageUploaded();
    } catch (e) {
      toast.error(e.message || 'Upload fehlgeschlagen');
    } finally { setUploadingCatId(null); }
  };

  const save = async () => {
    const validCats = categories.filter(c => c.name?.trim());
    if (!validCats.length) { toast.error('Mindestens einen Eintrag anlegen'); return; }
    if (!selectedLocIds.length) { toast.error('Mindestens einen Standort wählen'); return; }
    setSaving(true);
    try {
      if (existingBand) {
        // Kategorien aktualisieren
        await axios.put(`${API}/api/tasks/band/${existingBand.id}`,
          { title: title || undefined, categories: validCats.map((c, i) => ({ ...c, order: i })) },
          { headers: authH() });
        toast.success('Konfiguration gespeichert');
      } else {
        await axios.post(`${API}/api/tasks/band`, {
          location_ids: selectedLocIds,
          band_type: bandType,
          title: title || undefined,
          categories: validCats.map((c, i) => ({ ...c, order: i })),
        }, { headers: authH() });
        toast.success('Band angelegt');
      }
      onCreatedOrUpdated();
      setShowKonfig(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
    finally { setSaving(false); }
  };

  const deleteBand = async () => {
    if (!existingBand) return;
    if (!window.confirm('Band wirklich löschen? Der gesamte Fortschritt und die Historie gehen verloren.')) return;
    try {
      await axios.delete(`${API}/api/tasks/band/${existingBand.id}`, { headers: authH() });
      toast.success('Band gelöscht');
      onCreatedOrUpdated();
    } catch { toast.error('Fehler'); }
  };

  return (
    <div className="border border-border rounded-xl overflow-hidden" data-testid="admin-konfig-panel">
      <button onClick={() => setShowKonfig(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Konfigurieren (Admin/SL)</span>
          {existingBand && <span className="text-xs text-muted-foreground">· {existingBand.categories?.length || 0} Einträge</span>}
        </div>
        {showKonfig ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {showKonfig && (
        <div className="p-4 space-y-4 border-t border-border">
          {/* Standorte (nur beim Erstellen) */}
          {!existingBand && locations.length > 1 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Für welche Standorte?</p>
              <div className="flex flex-wrap gap-2">
                {locations.map(l => (
                  <button key={l.id} onClick={() => toggleLoc(l.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-colors ${
                      selectedLocIds.includes(l.id) ? 'border-primary bg-primary/10 text-primary' : 'border-border text-muted-foreground'
                    }`}>
                    <MapPin className="h-3.5 w-3.5" /> {l.name}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">Jeder Standort bekommt ein eigenes unabhängiges Band.</p>
            </div>
          )}

          {/* Titel */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Titel (optional)</p>
            <Input value={title} onChange={e => setTitle(e.target.value)}
              placeholder={bandType === 'auffuellen' ? 'z.B. Regal-Auffüllen' : 'z.B. Reinigungsrunde'}
              className="h-9 text-sm" />
          </div>

          {/* Einträge */}
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-2">
              {bandType === 'auffuellen' ? 'Regale / Kategorien' : 'Zonen / Bereiche'}
            </p>
            <CategoryList categories={categories} onChange={setCategories} />
          </div>

          {/* Referenzbild-Upload pro Kategorie (nur wenn Band bereits existiert) */}
          {existingBand && existingBand.categories?.length > 0 && (
            <div className="space-y-2 border-t pt-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Referenzbilder</p>
              {existingBand.categories.map(cat => (
                <div key={cat.id} className="flex items-center gap-2">
                  {cat.reference_image_url ? (
                    <img src={cat.reference_image_url} alt="" className="h-8 w-8 rounded object-cover border shrink-0"
                      onError={e => { e.target.style.display='none'; }} />
                  ) : (
                    <div className="h-8 w-8 rounded border bg-muted flex items-center justify-center shrink-0">
                      <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                  )}
                  <span className="text-sm flex-1 truncate">{cat.name}</span>
                  <button
                    type="button"
                    disabled={uploadingCatId === cat.id}
                    onClick={() => { const el = imgRefs.current[cat.id]; el && el.click(); }}
                    className="text-xs text-primary hover:underline shrink-0 flex items-center gap-1 disabled:opacity-50"
                  >
                    {uploadingCatId === cat.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                    {cat.reference_image_url ? 'Ändern' : 'Hochladen'}
                  </button>
                  <input
                    type="file" accept="image/*" className="hidden"
                    ref={el => { imgRefs.current[cat.id] = el; }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) uploadCategoryImage(cat.id, f); e.target.value = ''; }}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} className="flex-1 gap-2">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {existingBand ? 'Speichern' : 'Anlegen'}
            </Button>
            {existingBand && (
              <Button variant="outline" className="text-destructive border-destructive hover:bg-destructive/10"
                onClick={deleteBand}>
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Haupt-Export ──────────────────────────────────────────────────────────────
export default function BandPage({ bandType, pageTitle, icon: PageIcon }) {
  const { user } = useAuth();
  const isAdmin = ['admin', 'stationsleitung', 'superadmin'].includes(user?.role);

  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState('');
  const [band, setBand] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/user/locations`, { headers: authH() })
      .then(r => {
        const locs = r.data || [];
        setLocations(locs);
        const own = user?.location_ids?.length
          ? locs.find(l => user.location_ids.includes(l.id)) : null;
        setSelectedLocId(own?.id || locs[0]?.id || '');
      }).catch(() => toast.error('Standorte konnten nicht geladen werden'));
  }, [user?.id]);

  const fetchBand = useCallback(async () => {
    if (!selectedLocId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/tasks/band?location_id=${selectedLocId}&band_type=${bandType}`, { headers: authH() });
      setBand(r.data?.id ? r.data : null);
    } catch { setBand(null); }
    finally { setLoading(false); }
  }, [selectedLocId, bandType]);

  useEffect(() => { fetchBand(); }, [fetchBand]);

  const [lightbox, setLightbox] = useState(null);

  const bandStart = async (id) => {
    try { await axios.post(`${API}/api/tasks/band/${id}/start`, {}, { headers: authH() }); fetchBand(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  const bandFinish = async (id) => {
    try {
      await axios.post(`${API}/api/tasks/band/${id}/finish`, {}, { headers: authH() });
      fetchBand();
      toast.success('Weiter — nächster Eintrag!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Fehler'); }
  };

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto px-4 pb-8 space-y-5 pt-2" data-testid={`band-page-${bandType}`}>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-bold flex items-center gap-2">
            {PageIcon && <PageIcon className="h-5 w-5 text-primary" />}
            {pageTitle}
          </h1>
          <LocationSelector locations={locations} value={selectedLocId} onChange={setSelectedLocId} />
        </div>

        {/* Admin-Konfig (nur Admin/SL) */}
        {isAdmin && (
          <AdminKonfig
            bandType={bandType}
            locations={locations}
            currentLocationId={selectedLocId}
            existingBand={band}
            onCreatedOrUpdated={fetchBand}
            onRefImageUploaded={fetchBand}
          />
        )}

        {/* Teilen (Auffüllen + Reinigungsplan, nur Admin/SL) */}
        {isAdmin && (bandType === 'auffuellen' || bandType === 'putzen') && (
          <TagesgeschaeftShareBar
            locationId={selectedLocId}
            contentType={bandType}
            listLabel={bandType === 'putzen' ? 'Reinigungsplan-Liste' : 'Auffüllen-Liste'}
            onSent={fetchBand}
          />
        )}

        {/* Arbeitsansicht */}
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <BandArbeitsansicht band={band} onStart={bandStart} onFinish={bandFinish} onLightbox={setLightbox} />
        )}

        {/* Lightbox */}
        {lightbox && (
          <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}>
            <img src={lightbox} alt="Referenz" className="max-h-[85vh] max-w-full rounded-xl shadow-2xl object-contain" />
            <button onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 bg-white/20 text-white rounded-full p-2 hover:bg-white/30">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
